import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from '@angular/cdk/menu';
import {NgTemplateOutlet} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {AbilityServiceSignal} from '@casl/angular';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import {Optimus} from '@openng/optimus-ui/config';
import {DrawerModule} from '@openng/optimus-ui/drawer';
import {createTypedCan, ThemeStore} from '@scalified-monorepo/ng-shared';
import {AuthStore} from '@scalified-monorepo/ng-shared/auth';
import {FlagIconComponent} from '@scalified-monorepo/ng-shared/components';
import {SampleAppAbility} from '@scalified-monorepo/sample-shared';

@Component({
  selector: 'admin-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslocoDirective,
    FlagIconComponent,
    DrawerModule,
    NgTemplateOutlet,
    CdkMenu,
    CdkMenuItem,
    CdkMenuTrigger,
  ],
  template: `
    <ng-container *transloco="let transl">
      <!-- Mobile drawer (hidden on md+) -->
      <p-drawer
        class="md:hidden"
        [visible]="drawerVisible()"
        (onHide)="drawerHide.emit()"
        position="left"
        styleClass="!top-16 !w-64 !border-0 !p-0 !rounded-none"
        [maskStyle]="{top: '4rem', height: 'calc(100% - 4rem)', 'animation-duration': '0s'}">
        <ng-template pTemplate="headless">
          <aside
            aria-label="Sidenav"
            class="border-default bg-neutral-primary-soft relative h-[calc(100vh-4rem)] w-full border-e border-t">
            <ng-container *ngTemplateOutlet="sidebarContent"></ng-container>
          </aside>
        </ng-template>
      </p-drawer>

      <!-- Desktop sidebar (hidden on mobile) -->
      <aside
        aria-label="Sidenav"
        class="border-default bg-neutral-primary-soft fixed top-0 left-0 z-40 hidden h-screen w-64 border-e pt-14 md:block">
        <ng-container *ngTemplateOutlet="sidebarContent"></ng-container>
      </aside>

      <ng-template #sidebarContent>
        <div class="bg-neutral-primary-soft h-full overflow-y-auto px-3 py-5">
          <ul class="space-y-2">
            <li>
              @let canReadAccounts = ability.can('read', 'Account');
              @let canReadRoles = ability.can('read', 'Role');
              @let canReadResources = ability.can('read', 'DynamicResource');

              @if (canReadAccounts || canReadRoles || canReadResources) {
                <button
                  type="button"
                  class="group rounded-base text-heading hover:bg-neutral-quaternary flex w-full cursor-pointer items-center p-2 font-medium transition-colors duration-200"
                  (click)="accessControlOpen.set(!accessControlOpen())">
                  <svg
                    aria-hidden="true"
                    class="text-body group-hover:text-heading h-6 w-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clip-rule="evenodd"></path>
                  </svg>
                  <span class="ml-3 flex-1 text-left whitespace-nowrap">
                    {{ transl('_account_sidebar.accessControl') }}
                  </span>
                  <svg
                    aria-hidden="true"
                    class="h-6 w-6 transition-transform duration-200"
                    [class.rotate-180]="accessControlOpen()"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"></path>
                  </svg>
                </button>
                <ul id="dropdown-authentication" class="space-y-2 py-2" [class.hidden]="!accessControlOpen()">
                  @if (canReadAccounts) {
                    <li>
                      <a
                        class="group rounded-base hover:bg-neutral-quaternary flex items-center p-2 pl-11 transition-colors duration-200"
                        routerLink="/admin/auth-test"
                        routerLinkActive="bg-neutral-quaternary text-heading"
                        [routerLinkActiveOptions]="{exact: false}"
                        (click)="onNavLinkClick()">
                        Auth Test
                      </a>
                    </li>
                  }
                  @if (canReadAccounts) {
                    <li>
                      <a
                        class="group rounded-base hover:bg-neutral-quaternary flex items-center p-2 pl-11 transition-colors duration-200"
                        routerLink="/admin/access-management/accounts"
                        routerLinkActive="bg-neutral-quaternary text-heading"
                        [routerLinkActiveOptions]="{exact: false}"
                        (click)="onNavLinkClick()">
                        {{ transl('_account_sidebar.accountManagement') }}
                      </a>
                    </li>
                  }
                  @if (canReadRoles) {
                    <li>
                      <a
                        class="group rounded-base hover:bg-neutral-quaternary flex items-center p-2 pl-11 transition-colors duration-200"
                        routerLink="/admin/access-management/roles"
                        routerLinkActive="bg-neutral-quaternary text-heading"
                        [routerLinkActiveOptions]="{exact: false}"
                        (click)="onNavLinkClick()">
                        {{ transl('_account_sidebar.roleManagement') }}
                      </a>
                    </li>
                  }
                  @if (canReadResources) {
                    <li>
                      <a
                        class="group rounded-base hover:bg-neutral-quaternary flex items-center p-2 pl-11 transition-colors duration-200"
                        routerLink="/admin/access-management/resources"
                        routerLinkActive="bg-neutral-quaternary text-heading"
                        [routerLinkActiveOptions]="{exact: false}"
                        (click)="onNavLinkClick()">
                        {{ transl('_account_sidebar.resourceManagement') }}
                      </a>
                    </li>
                  }
                </ul>
              }
            </li>
          </ul>
          <ul class="border-default mt-5 space-y-2 border-t pt-5">
            <li>
              <a
                class="group rounded-base text-heading hover:bg-neutral-quaternary flex items-center p-2 font-medium transition-colors duration-200"
                routerLink="/account/help"
                routerLinkActive="bg-neutral-quaternary text-heading"
                [routerLinkActiveOptions]="{exact: true}"
                (click)="onNavLinkClick()">
                <svg
                  inert
                  class="text-body group-hover:text-heading h-6 w-6 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    clip-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                    fill-rule="evenodd"></path>
                </svg>
                <span class="ml-3">Help</span>
              </a>
            </li>
          </ul>
        </div>
        <!--Bottom buttons -->
        <div
          class="bg-neutral-primary-soft absolute bottom-0 left-0 z-20 flex w-full justify-center space-x-4 p-4 lg:flex">
          <button
            (click)="themeStore.toggleTheme()"
            class="rounded-base text-body hover:bg-neutral-tertiary-medium hover:text-heading focus:ring-neutral-quaternary-medium mr-2 inline-flex h-10 w-10 cursor-pointer items-center justify-center p-2.5 text-sm focus:ring-2 focus:outline-none"
            type="button">
            <svg
              inert
              class="inline h-4 w-4 dark:hidden"
              fill="currentColor"
              viewBox="0 0 18 20"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17.8 13.75a1 1 0 0 0-.859-.5A7.488 7.488 0 0 1 10.52 2a1 1 0 0 0 0-.969A1.035 1.035 0 0 0 9.687.5h-.113a9.5 9.5 0 1 0 8.222 14.247 1 1 0 0 0 .004-.997Z"></path>
            </svg>
            <svg
              inert
              class="hidden h-4 w-4 dark:inline"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 15a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-11a1 1 0 0 0 1-1V1a1 1 0 0 0-2 0v2a1 1 0 0 0 1 1Zm0 12a1 1 0 0 0-1 1v2a1 1 0 1 0 2 0v-2a1 1 0 0 0-1-1ZM4.343 5.757a1 1 0 0 0 1.414-1.414L4.343 2.929a1 1 0 0 0-1.414 1.414l1.414 1.414Zm11.314 8.486a1 1 0 0 0-1.414 1.414l1.414 1.414a1 1 0 0 0 1.414-1.414l-1.414-1.414ZM4 10a1 1 0 0 0-1-1H1a1 1 0 0 0 0 2h2a1 1 0 0 0 1-1Zm15-1h-2a1 1 0 1 0 0 2h2a1 1 0 0 0 0-2ZM4.343 14.243l-1.414 1.414a1 1 0 1 0 1.414 1.414l1.414-1.414a1 1 0 0 0-1.414-1.414ZM14.95 6.05a1 1 0 0 0 .707-.293l1.414-1.414a1 1 0 1 0-1.414-1.414l-1.414 1.414a1 1 0 0 0 .707 1.707Z"></path>
            </svg>
            <span class="sr-only">Toggle dark mode</span>
          </button>
          <a
            class="rounded-base text-body hover:bg-neutral-tertiary-medium hover:text-heading focus:ring-neutral-quaternary-medium mr-2 inline-flex h-10 w-10 cursor-pointer items-center justify-center p-2.5 text-sm focus:ring-2 focus:outline-none"
            data-tooltip-target="tooltip-settings"
            routerLink="./settings"
            (click)="onNavLinkClick()">
            <svg inert class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                clip-rule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                fill-rule="evenodd"></path>
            </svg>
          </a>
          <div
            class="tooltip rounded-base bg-dark-strong invisible absolute z-10 inline-block px-3 py-2 text-sm font-medium text-white opacity-0 shadow-sm transition-opacity duration-300"
            id="tooltip-settings"
            role="tooltip">
            Settings page
            <div class="tooltip-arrow" data-popper-arrow></div>
          </div>
          <button
            [cdkMenuTriggerFor]="languageMenu"
            class="rounded-base hover:bg-neutral-tertiary-medium focus:ring-neutral-quaternary-medium mr-2 inline-flex h-10 w-10 cursor-pointer items-center justify-center p-2.5 text-sm focus:ring-2 focus:outline-none"
            type="button">
            <flag-icon class="mt-0.5 h-5 w-5 rounded-full" [language]="currentLanguage"></flag-icon>
          </button>
          <!-- Language dropdown -->
          <ng-template #languageMenu>
            <div
              class="divide-default rounded-base border-default bg-neutral-secondary-soft z-50 m-2 list-none divide-y border text-base shadow"
              cdkMenu>
              <ul class="py-1" role="none">
                <li>
                  <button
                    cdkMenuItem
                    class="text-body hover:bg-neutral-tertiary hover:text-body-subtle block w-full cursor-pointer px-4 py-2 text-left text-sm"
                    (click)="changeLanguage('en')"
                    role="menuitem">
                    <div class="inline-flex items-center">
                      <flag-icon language="en" class="mr-2 h-3.5 w-3.5 rounded-full"></flag-icon>
                      English (US)
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    cdkMenuItem
                    class="text-body hover:bg-neutral-tertiary hover:text-body-subtle block w-full cursor-pointer px-4 py-2 text-left text-sm"
                    (click)="changeLanguage('ua')"
                    role="menuitem">
                    <div class="inline-flex items-center">
                      <flag-icon language="ua" class="mr-2 h-3.5 w-3.5 rounded-full"></flag-icon>
                      Українська
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </ng-template>
        </div>
      </ng-template>
    </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSidebarComponent implements OnInit {
  drawerVisible = input<boolean>(false);
  drawerHide = output<void>();

  accessControlOpen = signal(true);

  private translocoService = inject(TranslocoService);
  protected themeStore = inject(ThemeStore);
  protected optimusConfig = inject(Optimus);
  private authStore = inject(AuthStore);
  private abilityService = inject<AbilityServiceSignal<SampleAppAbility>>(AbilityServiceSignal);
  protected readonly ability = createTypedCan<SampleAppAbility>(this.abilityService);

  identity = this.authStore.identity;

  get initialRoute() {
    return ['/admin'];
  }

  get queryParams() {
    const queryParams: {param1?; param2?} = {};
    return queryParams;
  }

  currentLanguage: string | undefined;

  ngOnInit() {
    this.currentLanguage = this.translocoService.getActiveLang();
    this.translocoService.langChanges$.subscribe(lang => {
      this.currentLanguage = lang;
    });
  }

  changeLanguage(language: string) {
    this.translocoService.setActiveLang(language);
  }

  onNavLinkClick() {
    if (this.drawerVisible()) {
      this.drawerHide.emit();
    }
  }

  setOptimusTranslation(lang: string) {
    // Assuming your translation files have a '_optimus' section
    this.translocoService.selectTranslateObject('_optimus', {}, lang).subscribe(optimusTranslations => {
      console.log('Setting Optimus translations:', optimusTranslations);
      this.optimusConfig.setTranslation(optimusTranslations);
    });
  }
}
